import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { Button } from "@inteacc/ui";
const UnsavedChangesContext = createContext(null);
export const UnsavedChangesProvider = ({ children, defaultMessage = "You have unsaved changes. Discard them and continue?", }) => {
    const [entries, setEntries] = useState(() => new Map());
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const dialogRef = useRef(null);
    const register = useCallback((entry, id) => {
        setEntries((current) => {
            const next = new Map(current);
            if (entry)
                next.set(id, entry);
            else
                next.delete(id);
            return next;
        });
    }, []);
    const dirty = entries.size > 0;
    const message = [...entries.values()][0]?.message ?? defaultMessage;
    const requestNavigation = useCallback((proceed) => {
        if (!dirty) {
            proceed();
            return true;
        }
        setPendingNavigation(() => proceed);
        return false;
    }, [dirty]);
    useEffect(() => {
        if (!dirty)
            return undefined;
        const beforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = message;
            return message;
        };
        window.addEventListener("beforeunload", beforeUnload);
        return () => window.removeEventListener("beforeunload", beforeUnload);
    }, [dirty, message]);
    useEffect(() => {
        if (pendingNavigation) {
            dialogRef.current?.querySelector("button")?.focus();
        }
    }, [pendingNavigation]);
    const publicValue = useMemo(() => ({ dirty, message, requestNavigation }), [dirty, message, requestNavigation]);
    return (_jsx(UnsavedChangesRegistrationContext.Provider, { value: register, children: _jsxs(UnsavedChangesContext.Provider, { value: publicValue, children: [children, pendingNavigation ? (_jsx("div", { "aria-describedby": "iaf-unsaved-message", "aria-labelledby": "iaf-unsaved-title", "aria-modal": "true", className: "iaf-experience-dialog-backdrop", role: "alertdialog", children: _jsxs("div", { className: "iaf-experience-dialog", ref: dialogRef, children: [_jsx("h2", { id: "iaf-unsaved-title", children: "Unsaved changes" }), _jsx("p", { id: "iaf-unsaved-message", children: message }), _jsxs("div", { className: "iaf-experience-dialog__actions", children: [_jsx(Button, { onClick: () => setPendingNavigation(null), tone: "neutral", children: "Stay on page" }), _jsx(Button, { onClick: () => {
                                            const proceed = pendingNavigation;
                                            setPendingNavigation(null);
                                            proceed();
                                        }, tone: "danger", children: "Discard and continue" })] })] }) })) : null] }) }));
};
const UnsavedChangesRegistrationContext = createContext(null);
export const useUnsavedChange = (id, dirty, message = "You have unsaved changes. Discard them and continue?") => {
    const register = useContext(UnsavedChangesRegistrationContext);
    if (!register) {
        throw new Error("UnsavedChangesProvider is missing.");
    }
    useEffect(() => {
        register(dirty ? { id, message } : null, id);
        return () => register(null, id);
    }, [dirty, id, message, register]);
};
export const useUnsavedChanges = () => {
    const value = useContext(UnsavedChangesContext);
    if (!value) {
        throw new Error("UnsavedChangesProvider is missing.");
    }
    return value;
};
export const GuardedNavigationLink = ({ children, href, onNavigate, }) => {
    const { requestNavigation } = useUnsavedChanges();
    const navigate = () => {
        if (onNavigate)
            onNavigate(href);
        else
            window.location.assign(href);
    };
    return (_jsx("a", { href: href, onClick: (event) => {
            if (event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey) {
                return;
            }
            event.preventDefault();
            requestNavigation(navigate);
        }, children: children }));
};
//# sourceMappingURL=unsaved.js.map